import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Card, List, Space, Popconfirm, message, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FolderOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Title, Text } = Typography;

interface HotelGroup {
  id: number;
  name: string;
  description?: string;
  createdAt: string;
  hotels?: any[];
}

interface HotelGroupManagementProps {
  onSelectGroup: (groupId: number, groupName: string) => void;
}

const HotelGroupManagement: React.FC<HotelGroupManagementProps> = ({ onSelectGroup }) => {
  const [groups, setGroups] = useState<HotelGroup[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<HotelGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/hotel-groups`);
      setGroups(response.data);
    } catch (error: any) {
      message.error('Failed to load hotel groups');
    }
  };

  const handleCreate = () => {
    setEditingGroup(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleEdit = (group: HotelGroup) => {
    setEditingGroup(group);
    form.setFieldsValue({
      name: group.name,
      description: group.description,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/hotel-groups/${id}`);
      message.success('Hotel group deleted successfully');
      loadGroups();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete hotel group');
    }
  };

  const handleSubmit = async (values: { name: string; description?: string }) => {
    setLoading(true);
    try {
      if (editingGroup) {
        await axios.put(`${API_URL}/hotel-groups/${editingGroup.id}`, values);
        message.success('Hotel group updated successfully');
      } else {
        await axios.post(`${API_URL}/hotel-groups`, values);
        message.success('Hotel group created successfully');
      }
      setIsModalOpen(false);
      form.resetFields();
      loadGroups();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '32px 48px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 32,
        gap: 16,
      }}>
        <div>
          <Title level={2} style={{ margin: 0, marginBottom: 8 }}>Hotel Groups</Title>
          <Text type="secondary" style={{ fontSize: 15 }}>Manage your hotel groups and properties</Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          Create Hotel Group
        </Button>
      </div>

      {groups.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '48px 24px' }}>
            <FolderOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={4}>No hotel groups yet</Title>
            <Text type="secondary">Create your first hotel group to get started</Text>
            <br />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              style={{ marginTop: 16 }}
            >
              Create Hotel Group
            </Button>
          </div>
        </Card>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 4 }}
          dataSource={groups}
          renderItem={(group) => (
            <List.Item>
              <Card
                hoverable
                onClick={() => onSelectGroup(group.id, group.name)}
                style={{ height: '100%' }}
                actions={[
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(group);
                    }}
                  >
                    Edit
                  </Button>,
                  <Popconfirm
                    title="Delete hotel group?"
                    description="This will delete all hotels in this group"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleDelete(group.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Delete
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <Card.Meta
                  avatar={<FolderOutlined style={{ fontSize: 32, color: '#667eea' }} />}
                  title={group.name}
                  description={
                    <div>
                      <div>{group.description || 'No description'}</div>
                      <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                        {group.hotels?.length || 0} {group.hotels?.length === 1 ? 'hotel' : 'hotels'}
                      </Text>
                    </div>
                  }
                />
              </Card>
            </List.Item>
          )}
        />
      )}

      <Modal
        title={editingGroup ? 'Edit Hotel Group' : 'Create Hotel Group'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g., Europe Hotels, Asia Pacific" />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea
              placeholder="Optional description"
              rows={3}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                {editingGroup ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => {
                setIsModalOpen(false);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HotelGroupManagement;
